import { extractZipCodeFromString } from '@widget/core';
import {
  mapInterioirQuality,
  mapObjectCategory,
  mapObjectType,
  mapInvestmentRentActive,
} from './mappers';

import type { FetchValuationArgs } from './api';
import type { WidgetData } from './types';

/**
 * Returns a string in the format 'low - high €', where low and high are rounded to the closes thousand
 */
export function formatValuationPrice([low, high]: [number, number]): string {
  const formatter = new Intl.NumberFormat('de-DE');
  const priceStart = formatter.format(Math.round(low / 1000) * 1000);
  const priceEnd = formatter.format(Math.round(high / 1000) * 1000);
  return `${priceStart} – ${priceEnd} €`;
}

export function hasKeys(object: Record<string, unknown>, requiredKeys: string[]): boolean {
  return requiredKeys.every((arg: string) => arg in object);
}
/**
 * Takes in the data coming from the Widget and maps it to the format expected by the API.
 * Any keys that have their value equal to undefined are removed
 */
export function prepareFetchValuationArguments(
  widgetFormData: Record<string, unknown>,
): FetchValuationArgs {
  const data = widgetFormData as WidgetData;
  const {
    livingSpaceSize,
    property,
    constructionYear,
    numberRooms,
    propertyRented,
    zipCode,
    landAreaSize,
    propertySize,
    propertyType,
    qualityStandard,
  } = data;

  const mappedData: FetchValuationArgs = {
    id: 'defaultSessionID_WidgetX_MVP',
    address_geocoded_postalcode: extractZipCodeFromString(zipCode),
    object_category: mapObjectCategory(property),
    structure_area_living: livingSpaceSize,
    // Optional args
    construction_yearfinished: constructionYear ? Number(constructionYear) : undefined,
    plot_area_total: propertySize || landAreaSize,
    structure_rooms_living: numberRooms,
    interior_quality: qualityStandard && mapInterioirQuality(qualityStandard),
    object_type: propertyType && mapObjectType(propertyType),
    investment_rent_active: propertyRented && mapInvestmentRentActive(propertyRented),
  };

  return removeUndefinedKeys(mappedData);
}

export function removeUndefinedKeys<T>(object: T): T {
  return Object.fromEntries(
    Object.entries(object as any).filter(([_, value]) => value != undefined),
  ) as T;
}
